/* ★ ENJIN FLOW RESIPI — peraturan medan card (PLAN_FLOW_RESIPI.md, 15-17 Okt 2026).
 *
 * Module yang menuntut enjin `resipi` (scriptKey) dengan resipi "satu gambar untuk semua scene":
 *   • Image Prompt  = prompts.set1.promptImage   (SATU gambar AI, dipakai semua scene)
 *   • Video Prompt scene n = prompts.set1.promptVideo{n}; scene 1 = `promptVideo`, sandaran `promptVideo1`
 *   Apa yang tertulis dalam kotak = apa yang robot tampal ke Flow (tiada gabungan visual + dialog
 *   seperti card FLOWNITY — di sana `promptVideo{n}` ialah prompt GAMBAR; jangan campur dua bentuk).
 *
 * Resipi "gambar berlainan setiap scene" (31 Okt): Image Prompt scene 1 = promptImage (sandaran promptImage1),
 *   scene n = promptImage{n}. Bentuk lain sama.
 *
 * ⚠️ KEMBAR dengan skrip robot REMOTE `sesaat (extension)/src/chrome/flow-resipi/20-resipi.js`
 *    (`_resipiKiraBabak`, `_resipiBabakUntukSet`, `_resipiImejUntukSet`). Ubah satu = ubah dua-dua, kalau tidak card memapar /
 *    semakan meluluskan teks yang robot tidak baca.
 *
 * TULEN — tiada React, tiada Firestore (diuji dengan node --test).
 */

export const RESIPI_BABAK_MAKS = 5;
export const RESIPI_GAMBAR_SAHAJA_MAKS = 4;   // Images only: 1–4 gambar, satu set (KEMBAR 20-resipi.js)
// 3 scene ke atas = SATU set per card (keputusan user 3 Okt); 1–2 scene = semua set yang dipilih (sampai 3).
// ⚠️ KEMBAR robot 30-enjin.js (`resipi.babak >= 3 && batchTasks.length >= 1` → set selebihnya dilangkau).
export const RESIPI_SET_TUNGGAL_DARI = 3;

const adaTeks = (v) => typeof v === 'string' && v.trim() !== '';

/** Bilangan scene yang robot jalankan (dikepit 1–5, sama seperti robot). */
export function bilBabakResipi(sceneCount) {
    const n = parseInt(sceneCount, 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(n, RESIPI_BABAK_MAKS) : 1;
}

/** Teks Video Prompt scene n — TEPAT seperti robot membacanya. */
export function teksVideoBabak(set, n) {
    const s = (set && typeof set === 'object') ? set : {};
    if (Number(n) === 1) return adaTeks(s.promptVideo) ? s.promptVideo : (typeof s.promptVideo1 === 'string' ? s.promptVideo1 : '');
    const v = s['promptVideo' + n];
    return typeof v === 'string' ? v : '';
}

/** Medan yang ditulis bila pelanggan mengedit scene n — medan yang robot BACA (scene 1: yang sedang berisi). */
export function medanVideoBabak(set, n) {
    const s = (set && typeof set === 'object') ? set : {};
    if (Number(n) === 1) return (adaTeks(s.promptVideo) || !adaTeks(s.promptVideo1)) ? 'promptVideo' : 'promptVideo1';
    return 'promptVideo' + n;
}

/** Teks Image Prompt scene n (resipi gambar berlainan) — TEPAT seperti robot membacanya. */
export function teksImejBabak(set, n) {
    const s = (set && typeof set === 'object') ? set : {};
    if (Number(n) === 1) return adaTeks(s.promptImage) ? s.promptImage : (typeof s.promptImage1 === 'string' ? s.promptImage1 : '');
    const v = s['promptImage' + n];
    return typeof v === 'string' ? v : '';
}

/** Medan yang ditulis bila pelanggan mengedit Image Prompt scene n. */
export function medanImejBabak(set, n) {
    const s = (set && typeof set === 'object') ? set : {};
    if (Number(n) === 1) return (adaTeks(s.promptImage) || !adaTeks(s.promptImage1)) ? 'promptImage' : 'promptImage1';
    return 'promptImage' + n;
}

/** Apa yang kurang untuk satu set: { gambar: bool, babakKosong: [nombor scene], gambarKosong: [nombor scene] }.
 *  `jenisGambar` 'berlainan' = setiap scene perlukan Image Prompt sendiri · 'sahaja' = Images only, Image Prompt setiap gambar tanpa Video Prompt · 'frame' = Before and after, dua Image Prompt + satu Video Prompt · 'tiada' = text to video · 'sama' = satu. */
export function kurangResipi(set, bilBabak, jenisGambar = 'sama') {
    const s = (set && typeof set === 'object') ? set : {};
    const sahaja = jenisGambar === 'sahaja';
    const frame = jenisGambar === 'frame';
    const n = frame ? 2 : (sahaja ? Math.min(bilBabakResipi(bilBabak), RESIPI_GAMBAR_SAHAJA_MAKS) : bilBabakResipi(bilBabak));
    const ikutScene = jenisGambar === 'berlainan' || sahaja || frame;
    const babakKosong = [];
    const gambarKosong = [];
    for (let b = 1; b <= n; b++) {
        if (!sahaja && !(frame && b > 1) && !adaTeks(teksVideoBabak(s, b))) babakKosong.push(b);
        if (ikutScene && !adaTeks(teksImejBabak(s, b))) gambarKosong.push(b);
    }
    const gambar = jenisGambar === 'tiada' ? false : (ikutScene ? gambarKosong.length > 0 : !adaTeks(s.promptImage));
    return { gambar, babakKosong, gambarKosong };
}

/** Set yang robot BENAR-BENAR jalankan: 3+ scene → set pertama sahaja; 1–2 scene → semua.
 *  `siap` = set dilangkau (Continue Collection — sudah dihantar). `sets` mesti tersusun. */
export function setDijalankanResipi(sets, bilBabak, siap = [], jenisGambar = 'sama') {
    const langkau = new Set(Array.isArray(siap) ? siap : []);
    const tinggal = (Array.isArray(sets) ? sets : []).filter((k) => typeof k === 'string' && k && !langkau.has(k));
    return (jenisGambar === 'sahaja' || bilBabakResipi(bilBabak) >= RESIPI_SET_TUNGGAL_DARI) ? tinggal.slice(0, 1) : tinggal;
}
